import React from 'react';
import Avatar, { LoadableAvatar, getFallbackFromName } from '../../../v2BaseComponents/Avatar';
import useMedicalProfileOwner from '../../../../hooks/api/useMedicalProfileOwner';
import { useCollaborators } from '../../../../hooks/api/useCollaborators';
import urls from '../../../../utils/urls';
import { useHasPermission, useIsLoadingPermissions } from '../../../v2BaseComponents/Permissions/hooks';
import { isOrganizationCollaborator, isUserCollaborator } from '../../../../utils/collaboratorUtils';
import ProfilesProvider from './ProfilesProvider';

export type AvatarSubject = 'user' | 'profile' | 'collaborator';

export type UserAvatarProps = {
    subject: 'user';
    userId: string;
    organizationId: string | null;
    organizationName: string | null;
};

export type ProfileAvatarProps = {
    subject: 'profile';
};

export type CollaboratorAvatarProps = {
    subject: 'collaborator';
    collaboratorId: string;
};

export type OrganizationAvatarProps = {
    subject: 'organization';
    organizationId: string;
    organizationName: string;
};

export type AvatarSubjectProps =
    | UserAvatarProps
    | ProfileAvatarProps
    | CollaboratorAvatarProps
    | OrganizationAvatarProps;

export type LogCardAvatarOwnProps<TSubjectProps extends AvatarSubjectProps> = {
    name: string;
    profileId: string;
} & TSubjectProps;

export type LogCardAvatarProps<TSubjectProps extends AvatarSubjectProps = AvatarSubjectProps> =
    LogCardAvatarOwnProps<TSubjectProps>;

const renderAvatar = (isLoading: boolean, name: string) => {
    return <Avatar size="sm" isLoading={isLoading} fallbackName={getFallbackFromName(name)} image={null} />;
};

const LogCardLoadableAvatar: React.FC<LogCardAvatarProps & { avatarUrl: string }> = (props) => {
    return <LoadableAvatar size="sm" fallbackName={getFallbackFromName(props.name)} avatarUrl={props.avatarUrl} />;
};

const UserCollaboratorAvatar: React.FC<LogCardAvatarProps<UserAvatarProps>> = (props) => {
    const { collaborators, isLoadingCollaborators } = useCollaborators({
        medicalProfileId: props.profileId,
        disableInviteLoad: true,
    });
    const targetCollaborator = collaborators.find((collaborator) => {
        if (isUserCollaborator(collaborator)) {
            return collaborator.user.userId === props.userId;
        } else if (isOrganizationCollaborator(collaborator)) {
            return collaborator.organization.organizationId === props.organizationId;
        } else {
            return false;
        }
    });

    if (isLoadingCollaborators) {
        return renderAvatar(true, props.name);
    } else if (targetCollaborator) {
        return (
            <LogCardLoadableAvatar
                avatarUrl={urls.api.collaborators.collaboratorAvatar(
                    props.profileId,
                    targetCollaborator.medicalProfileCollaboratorId,
                )}
                {...props}
            />
        );
    } else {
        return renderAvatar(false, props.name);
    }
};

const UserAvatar: React.FC<LogCardAvatarProps<UserAvatarProps & { canViewCollaborators: boolean }>> = (props) => {
    const { isLoading, owner } = useMedicalProfileOwner({ medicalProfileId: props.profileId });

    if (isLoading) {
        return renderAvatar(true, props.name);
    } else if (owner && owner.userId === props.userId) {
        return <LogCardLoadableAvatar avatarUrl={urls.api.medicalProfiles.owner.avatar(props.profileId)} {...props} />;
    } else if (props.canViewCollaborators) {
        return <UserCollaboratorAvatar {...props} />;
    } else {
        return renderAvatar(false, props.name);
    }
};

const ProfileAvatar: React.FC<LogCardAvatarProps<ProfileAvatarProps>> = (props) => {
    return <LogCardLoadableAvatar avatarUrl={urls.api.medicalProfiles.avatar(props.profileId)} {...props} />;
};

const CollaboratorAvatar: React.FC<LogCardAvatarProps<CollaboratorAvatarProps>> = (props) => {
    const { collaborators, isLoadingCollaborators } = useCollaborators({
        medicalProfileId: props.profileId,
        disableInviteLoad: true,
    });
    const targetCollaborator = collaborators.find(
        (collaborator) => collaborator.medicalProfileCollaboratorId === props.collaboratorId,
    );

    if (isLoadingCollaborators) {
        return renderAvatar(true, props.name);
    } else if (targetCollaborator) {
        return (
            <LogCardLoadableAvatar
                avatarUrl={urls.api.collaborators.collaboratorAvatar(
                    props.profileId,
                    targetCollaborator.medicalProfileCollaboratorId,
                )}
                {...props}
            />
        );
    } else {
        return renderAvatar(false, props.name);
    }
};

const OrganizationAvatar: React.FC<LogCardAvatarProps<OrganizationAvatarProps>> = (props) => {
    const { collaborators, isLoadingCollaborators } = useCollaborators({
        medicalProfileId: props.profileId,
        disableInviteLoad: true,
    });
    const targetCollaborator = collaborators.find((collaborator) => {
        if (isOrganizationCollaborator(collaborator))
            return collaborator.organization.organizationId === props.organizationId;
        else return false;
    });

    if (isLoadingCollaborators) {
        return renderAvatar(true, props.name);
    } else if (targetCollaborator) {
        return (
            <LogCardLoadableAvatar
                avatarUrl={urls.api.collaborators.collaboratorAvatar(
                    props.profileId,
                    targetCollaborator.medicalProfileCollaboratorId,
                )}
                {...props}
            />
        );
    } else {
        return renderAvatar(false, props.name);
    }
};

const LogCardAvatar: React.FC<LogCardAvatarProps> = (props) => {
    const isLoadingPermissions = useIsLoadingPermissions(props.profileId);
    const canViewCollaborators = useHasPermission(props.profileId, 'collaborators', 'view');

    if (isLoadingPermissions) {
        return renderAvatar(true, props.name);
    }

    return (
        <ProfilesProvider>
            {(isLoadingProfiles, profiles) => {
                if (isLoadingProfiles) {
                    return renderAvatar(true, props.name);
                }

                const hasAccess = profiles.some((profile) => profile.medicalProfileId === props.profileId);
                if (!hasAccess) {
                    return renderAvatar(false, props.name);
                } else if (props.subject === 'user') {
                    return <UserAvatar {...props} canViewCollaborators={canViewCollaborators} />;
                } else if (props.subject === 'profile') {
                    return <ProfileAvatar {...props} />;
                } else if (props.subject === 'collaborator') {
                    return <CollaboratorAvatar {...props} />;
                } else {
                    return <OrganizationAvatar {...props} />;
                }
            }}
        </ProfilesProvider>
    );
};

export default React.memo(LogCardAvatar);
